import { Fragment } from "react";
import { Link } from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import {
  Button,
  ButtonTypeEnum,
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@tenant/ui";
import type { UsePricePredictionResponse } from "../PricePredictionCard/hooks/usePricePrediction";
import {
  type IntervalKey,
  PricePredictionInterval,
} from "../PricePredictionInterval/PricePredictionInterval";
import { usePricePredictionDialog } from "./hooks/usePricePredictionDialog";
import styles from "./styles.module.scss";

interface PricePredictionDialogProps
  extends DialogProps,
    UsePricePredictionResponse {
  className: string;
  watchTripAction: () => void;
}

export const PricePredictionDialog = ({
  className,
  open,
  onClose,
  onOpen,
  children,
  watchTripAction,
  priceDropDialogProps,
  ...props
}: PricePredictionDialogProps): JSX.Element => {
  const [block, cn] = useModuleBEM(styles, "PricePredictionDialog");
  const {
    t,
    brand: { urls },
  } = useI18nContext();
  const {
    context: {
      isPDPEligible,
      priceDropAmountPrice,
      currentLowestPrice,
      forecastMaxPrice,
      forecastMinPrice,
      shouldWait,
      intervals = {},
      lowestPriceSuffix,
    },
  } = usePricePredictionDialog();

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen}>
      <DialogTrigger className={className}>{children}</DialogTrigger>
      <DialogContent
        className={block}
        {...props}
        title={t("pricePrediction.header")}
        bottomFixedContent={
          <div className={cn("footer")}>
            <button
              className={cn("close-button")}
              type="button"
              onClick={onClose}
            >
              {t("close.button")}
            </button>
          </div>
        }
      >
        <p className={cn("subtitle", ["first"])}>{t("howToUse")}</p>

        <p className={cn("description")}>
          {isPDPEligible ? (
            <Trans
              i18nKey="flightShopPrediction.description"
              values={{ travelCreditAmount: priceDropAmountPrice }}
            />
          ) : (
            <Trans
              i18nKey="flightShopPrediction.descriptionWithoutPriceDrop"
              values={{ travelCreditAmount: priceDropAmountPrice }}
            />
          )}
        </p>
        <div className={cn("divider")} />
        <p className={cn("subtitle")}>
          {t("flightShopPrediction.lowestPriceTitle")}
        </p>

        <p className={cn("description")}>
          <span className={cn("lowest-price")}>{currentLowestPrice}</span>
          {lowestPriceSuffix}
        </p>
        <div className={cn("divider")} />
        {shouldWait ? (
          <>
            <p className={cn("subtitle")}>
              {t("pricePrediction.forecastedLowest")}
            </p>

            <p className={cn("description")}>
              <span className={cn("lowest-price")}>{forecastMinPrice}</span>
              {lowestPriceSuffix}
            </p>
          </>
        ) : (
          <>
            <p className={cn("subtitle")}>
              {t("pricePrediction.forecastedHighest")}
            </p>

            <p className={cn("description")}>
              <span className={cn("highest-price")}>{forecastMaxPrice}</span>
              {lowestPriceSuffix}
            </p>
          </>
        )}
        <div className={cn("divider")} />
        {/* Show Intervals */}
        {intervals &&
          Object.keys(intervals).map((key) => (
            <Fragment key={key}>
              <PricePredictionInterval
                intervalKey={key as IntervalKey}
                interval={intervals[key]}
              />
              <div className={cn("divider")} />
            </Fragment>
          ))}
        <div className={cn("pw")}>
          <div className={cn("pw-left")}>
            <p className={cn("pw-title")}>
              {t("core-flights.shopPage.notReadyToBook")}
            </p>
            <p className={cn("pw-description")}>{t("priceWatch.copy")}</p>
          </div>
          <Button buttonType={ButtonTypeEnum.Link} onClick={watchTripAction}>
            {t("priceWatch.watchCta")}
          </Button>
        </div>
        <div className={cn("divider")} />
        <p className={cn("disclaimer")}>
          <Trans
            i18nKey="flightShopPrediction.disclaimer"
            components={[
              <Link
                className={cn("terms-and-conditions-link")}
                to={urls.pricePredictionTerms}
                target="_blank"
                rel="noopener noreferrer"
              />,
            ]}
          />
        </p>
      </DialogContent>
    </Dialog>
  );
};
